/**
 * Resolve the event type id the booking APIs require.
 *
 * `event_type_id` is an analytics tag, not a permission — the booking service
 * never validates it, never joins it, and only forwards it to the session-event
 * API as a BackgroundTask that logs and swallows every failure. Authorization
 * comes from the JWT (resolve_caller_scope → employee ownership) instead.
 *
 * Resolution is by *description*, never by literal UUID: the ids differ between
 * the dev and prod auth databases, which is what makes the two hardcoded UUIDs
 * elsewhere in this repo latent bugs.
 *
 * TODO(bookings): drop INTERIM_EVENT_DESCRIPTION and the warning once the auth
 * service registers a booking event type for company_name=flexcellence /
 * application_name=flexboard. No other change is needed — the real description
 * is already checked first, so it takes over the moment it exists.
 */

/** What we want: a type that actually means "coach published availability". */
const BOOKING_EVENT_DESCRIPTION = "modify_coach_slot";

/**
 * What we send until that exists. Matches established practice in this app —
 * the workouts feature sends `modify_user_profile` on seven endpoints, reads
 * included.
 */
const INTERIM_EVENT_DESCRIPTION = "modify_user_profile";

/** Module-level so the warning fires once per page load, not once per render. */
let warnedAboutFallback = false;

/** Reset the one-shot warning latch. Tests only. */
export function __resetEventTypeWarning() {
  warnedAboutFallback = false;
}

/**
 * @param {Object} sessionUtils - From `useUtils()`; shape
 *   `{ event_types: [{ event_types: [{ id, description }] }] }`.
 * @returns {string|null} The event type id, or null when neither description
 *   is registered — callers must treat null as "not configured" and skip the
 *   request rather than sending a placeholder.
 */
export function resolveBookingEventTypeId(sessionUtils) {
  // flatMap every group rather than assuming [0], matching resolveRecipeEventTypeId.
  const types = (sessionUtils?.event_types || []).flatMap(
    (group) => group?.event_types || []
  );

  const real = types.find((t) => t?.description === BOOKING_EVENT_DESCRIPTION);
  if (real?.id) return real.id;

  const interim = types.find((t) => t?.description === INTERIM_EVENT_DESCRIPTION);
  if (interim?.id) {
    if (!warnedAboutFallback) {
      warnedAboutFallback = true;
      console.warn(
        `[Bookings] No '${BOOKING_EVENT_DESCRIPTION}' event type is registered; ` +
          `falling back to '${INTERIM_EVENT_DESCRIPTION}'. Booking activity will be ` +
          `mis-attributed in session-event analytics until the backend registers it.`
      );
    }
    return interim.id;
  }

  return null;
}
